// ===========================================
// COMPONENTE: VerDetallesVencimientoDialog
// ===========================================
// Dialog para visualizar detalles completos de un vencimiento

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { VencimientoConCliente } from '@/types/vencimientos'
import { ESTADOS_VENCIMIENTO_FISCAL } from '@/types/vencimientos'
import { getImpuestoColorClasses } from '@/lib/impuestoColorResolver'
import { formatearDiaCalendario } from '@/lib/diaCalendario'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { Calendar, DollarSign, FileText, User, Clock, Receipt, FileSpreadsheet, Pencil } from 'lucide-react'

type ExtendedVencimiento = VencimientoConCliente & {
  monto_determinado?: number | null
  monto_pagado?: number | null
  fecha_pago?: string | null
  forma_pago?: string | null
  referencia_pago?: string | null
  vep_numero?: string | null
  vep_monto?: number | null
  vep_fecha_vencimiento?: string | null
  plan_pago_cuotas?: number | null
  plan_pago_referencia?: string | null
  saldo_pendiente?: number | null
  estado_efectivo?: string | null
}

interface VerDetallesVencimientoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vencimiento: VencimientoConCliente | null
  onRegistrarPago?: (vencimiento: VencimientoConCliente) => void
  onRegistrarVEP?: (vencimiento: VencimientoConCliente) => void
  onPlanPago?: (vencimiento: VencimientoConCliente) => void
  onMarcarPresentado?: (vencimiento: VencimientoConCliente) => void
  onEditar?: (vencimiento: VencimientoConCliente) => void
}

const formatMonto = (monto: number) =>
  `$${Math.abs(monto).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export function VerDetallesVencimientoDialog({
  open,
  onOpenChange,
  vencimiento,
  onRegistrarPago,
  onRegistrarVEP,
  onPlanPago,
  onMarcarPresentado,
  onEditar,
}: VerDetallesVencimientoDialogProps) {
  if (!vencimiento) return null

  const v = vencimiento as ExtendedVencimiento
  const estado = v.estado_efectivo ?? v.estado ?? 'pendiente'
  const estadoConfig = ESTADOS_VENCIMIENTO_FISCAL[estado as keyof typeof ESTADOS_VENCIMIENTO_FISCAL]
  const tieneMonto = v.monto_determinado != null && v.monto_determinado > 0
  const diaPresentado = formatearDiaCalendario(v.fecha_presentacion)
  const diaPagado = formatearDiaCalendario(v.fecha_pago)

  const getTitulo = () => {
    if (estado === 'pagado') return 'Vencimiento Pagado'
    if (estado === 'plan_pago') return 'Vencimiento en Plan de Pago'
    if (estado === 'presentado') return 'Vencimiento Presentado'
    if (estado === 'vencido') return 'Vencimiento Vencido'
    return 'Detalle de Vencimiento'
  }

  const getTituloIcon = () => {
    if (estado === 'pagado') return <DollarSign className="h-5 w-5 text-blue-600" />
    if (estado === 'plan_pago') return <FileSpreadsheet className="h-5 w-5 text-violet-600" />
    if (estado === 'vencido') return <Clock className="h-5 w-5 text-red-600" />
    return <FileText className="h-5 w-5 text-green-600" />
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getTituloIcon()}
            {getTitulo()}
          </DialogTitle>
          <DialogDescription>
            Detalles del vencimiento fiscal
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Client & Tax Info */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{vencimiento.cliente_nombre}</span>
            </div>
            <div className="flex items-center gap-2">
              {vencimiento.tipo_impuesto && (
                <Badge variant="outline" className={getImpuestoColorClasses(vencimiento.tipo_impuesto)}>
                  {vencimiento.tipo_impuesto}
                </Badge>
              )}
              {estadoConfig && (
                <Badge variant="outline" className={`${estadoConfig.color} ${estadoConfig.bgColor} border-0`}>
                  {estadoConfig.label}
                </Badge>
              )}
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid gap-3 pt-2 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Período</span>
              <span className="text-sm font-medium">{vencimiento.periodo}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Fecha de Vencimiento
              </span>
              <span className="text-sm font-medium">
                {format(parseISO(vencimiento.fecha_vencimiento ?? ''), "d 'de' MMMM 'de' yyyy", { locale: es })}
              </span>
            </div>
          </div>

          {/* Presentación */}
          {(estado === 'presentado' || estado === 'pagado' || estado === 'plan_pago') && (
            <div className="grid gap-3 pt-2 border-t bg-green-50 dark:bg-green-950/20 p-3 rounded-lg">
              <h4 className="font-medium text-sm text-green-800 dark:text-green-400 flex items-center gap-1">
                <Clock className="h-4 w-4" />
                Presentación
              </h4>
              {diaPresentado && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Fecha de Presentación</span>
                  <span className="text-sm font-medium text-green-700 dark:text-green-400">
                    {diaPresentado}
                  </span>
                </div>
              )}
              {vencimiento.numero_presentacion && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Número de Presentación</span>
                  <span className="text-sm font-medium font-mono text-green-700 dark:text-green-400">
                    {vencimiento.numero_presentacion}
                  </span>
                </div>
              )}
              {v.monto_determinado != null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    Monto Determinado
                  </span>
                  <span className={`text-sm font-medium ${
                    v.monto_determinado === 0
                      ? 'text-muted-foreground'
                      : v.monto_determinado > 0
                        ? 'text-red-600'
                        : 'text-green-700'
                  }`}>
                    {v.monto_determinado === 0
                      ? 'Sin movimiento'
                      : `${v.monto_determinado > 0 ? '' : '-'}${formatMonto(v.monto_determinado)}`
                    }
                  </span>
                </div>
              )}
              {v.monto_determinado != null && v.monto_determinado !== 0 && (
                <p className="text-xs text-green-700/70 dark:text-green-400/70">
                  {v.monto_determinado > 0 ? 'Saldo a pagar' : 'Saldo a favor'}
                </p>
              )}
            </div>
          )}

          {/* VEP Info */}
          {v.vep_numero && (
            <div className="grid gap-3 pt-2 border-t bg-amber-50 dark:bg-amber-950/20 p-3 rounded-lg">
              <h4 className="font-medium text-sm text-amber-800 dark:text-amber-400 flex items-center gap-1">
                <Receipt className="h-4 w-4" />
                Volante Electrónico de Pago
              </h4>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Número VEP</span>
                <span className="text-sm font-medium font-mono">{v.vep_numero}</span>
              </div>
              {v.vep_monto != null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Monto VEP</span>
                  <span className="text-sm font-medium">{formatMonto(v.vep_monto)}</span>
                </div>
              )}
              {v.vep_fecha_vencimiento && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Vence</span>
                  <span className="text-sm font-medium">
                    {format(parseISO(v.vep_fecha_vencimiento), 'dd/MM/yyyy')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Pago Info */}
          {estado === 'pagado' && diaPagado && (
            <div className="grid gap-3 pt-2 border-t bg-blue-50 dark:bg-blue-950/20 p-3 rounded-lg">
              <h4 className="font-medium text-sm text-blue-800 dark:text-blue-400 flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                Pago Registrado
              </h4>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Fecha de Pago</span>
                <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                  {diaPagado}
                </span>
              </div>
              {v.monto_pagado != null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Monto Pagado</span>
                  <span className="text-sm font-medium">{formatMonto(v.monto_pagado)}</span>
                </div>
              )}
              {v.forma_pago && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Forma de Pago</span>
                  <span className="text-sm font-medium">{v.forma_pago}</span>
                </div>
              )}
              {v.referencia_pago && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Referencia</span>
                  <span className="text-sm font-medium font-mono">{v.referencia_pago}</span>
                </div>
              )}
            </div>
          )}

          {/* Plan de Pago Info */}
          {estado === 'plan_pago' && (
            <div className="grid gap-3 pt-2 border-t bg-violet-50 dark:bg-violet-950/20 p-3 rounded-lg">
              <h4 className="font-medium text-sm text-violet-800 dark:text-violet-400 flex items-center gap-1">
                <FileSpreadsheet className="h-4 w-4" />
                Plan de Pago
              </h4>
              {v.plan_pago_cuotas != null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Cuotas</span>
                  <span className="text-sm font-medium">{v.plan_pago_cuotas}</span>
                </div>
              )}
              {v.plan_pago_referencia && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Referencia</span>
                  <span className="text-sm font-medium">{v.plan_pago_referencia}</span>
                </div>
              )}
            </div>
          )}

          {/* Saldo */}
          {v.saldo_pendiente != null && v.saldo_pendiente > 0 && estado !== 'pagado' && (
            <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
              <span className="text-sm font-medium text-red-800 dark:text-red-400">Saldo Pendiente</span>
              <span className="text-sm font-bold text-red-600">{formatMonto(v.saldo_pendiente)}</span>
            </div>
          )}

          {/* Observaciones */}
          {vencimiento.observaciones && (
            <div className="pt-2 border-t">
              <span className="text-sm text-muted-foreground block mb-1">Observaciones</span>
              <p className="text-sm bg-muted p-3 rounded-lg">{vencimiento.observaciones}</p>
            </div>
          )}

          {/* Responsable */}
          {vencimiento.cliente_responsable && (
            <div className="pt-2 border-t">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-3 w-3" />
                Responsable: <span className="font-medium">{vencimiento.cliente_responsable}</span>
              </div>
            </div>
          )}

          {/* Edit Button */}
          {onEditar && (estado === 'pendiente' || estado === 'vencido' || estado === 'presentado') && (
            <div className="pt-2 border-t">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  onOpenChange(false)
                  onEditar(vencimiento)
                }}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Editar
              </Button>
            </div>
          )}

          {/* Contextual Action Buttons */}
          {(estado === 'pendiente' || estado === 'vencido' || (estado === 'presentado' && tieneMonto)) && (
            <div className="pt-2 border-t flex flex-wrap gap-2">
              {(estado === 'pendiente' || estado === 'vencido') && onMarcarPresentado && (
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    onOpenChange(false)
                    onMarcarPresentado(vencimiento)
                  }}
                >
                  Presentar
                </Button>
              )}
              {estado === 'presentado' && tieneMonto && onRegistrarPago && (
                <Button
                  size="sm"
                  onClick={() => {
                    onOpenChange(false)
                    onRegistrarPago(vencimiento)
                  }}
                >
                  <DollarSign className="h-4 w-4 mr-1" />
                  Registrar Pago
                </Button>
              )}
              {estado === 'presentado' && tieneMonto && onRegistrarVEP && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onOpenChange(false)
                    onRegistrarVEP(vencimiento)
                  }}
                >
                  <Receipt className="h-4 w-4 mr-1" />
                  VEP
                </Button>
              )}
              {estado === 'presentado' && tieneMonto && onPlanPago && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onOpenChange(false)
                    onPlanPago(vencimiento)
                  }}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-1" />
                  Plan de Pago
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
